import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/useAuthStore';
import { useFeatureToggleStore } from '@/stores/useFeatureToggleStore';
import { ModuleKey as LegacyModuleKey } from '@/types/featureToggle';
import { entitlementsApi, type MyEntitlements } from '@/services/entitlementsApi';

/**
 * Module keys a tenant can have enabled. Matches `docs/ARCHITECTURE.md` §5.
 * Core modules (patients, visits, rx, attachments, staff, settings) are
 * always on and not represented here.
 */
export type ModuleKey =
  | 'scheduling'
  | 'walkIn'
  | 'whatsappBot'
  | 'telegramBot'
  | 'dicomViewer'
  | 'brandedRx'
  | 'externalLab'
  | 'externalImaging'
  | 'customProtocols'
  | 'reports'
  | 'resourceScheduling'
  | 'multiSpecialty'
  | 'customRoles'
  | 'visitingDoctors';

export type PlanKey = 'free' | 'starter' | 'pro' | 'clinic' | 'enterprise';
export type TenantMode = 'solo' | 'practice' | 'clinic';

export interface TenantEntitlements {
  /** Every module toggle the current tenant sees. `true` = usable in UI. */
  modules: Record<ModuleKey, boolean>;
  plan: PlanKey;
  /** Derived from state; see docs/ARCHITECTURE.md §2. */
  mode: TenantMode;
  /** True while the entitlement snapshot is being fetched. Consumers can
   *  show skeleton state instead of flashing the free-tier default. */
  isLoading: boolean;
  /** Raw backend snapshot when available — surface page can use it for
   *  caps/usage details without a second fetch. */
  raw: MyEntitlements | null;
}

const FREE_DEFAULT: Record<ModuleKey, boolean> = {
  scheduling: true,
  walkIn: true,
  whatsappBot: true,
  telegramBot: false,
  dicomViewer: true,
  brandedRx: false,
  externalLab: false,
  externalImaging: false,
  customProtocols: false,
  reports: false,
  resourceScheduling: false,
  multiSpecialty: false,
  customRoles: false,
  visitingDoctors: false,
};

/**
 * Maps Architecture-level module keys to the `ModuleKey` enum used by
 * `useFeatureToggleStore`. Where a mapping exists, the clinic's module
 * setting is ANDed with the entitlement — it can withhold a module the
 * plan allows, never grant one it doesn't. Where no mapping exists we
 * fall back to the backend entitlement booleans, then to free-tier
 * defaults.
 */
const LEGACY_MAP: Partial<Record<ModuleKey, LegacyModuleKey>> = {
  scheduling: LegacyModuleKey.APPOINTMENTS,
  externalLab: LegacyModuleKey.LAB_ORDERS,
  externalImaging: LegacyModuleKey.IMAGING_ORDERS,
};

/**
 * Derive the FE plan label from the backend `isFreeTier` + `seatsPurchased`.
 * Backend deliberately doesn't surface tier strings to the EMR (auth
 * controller comment: "EMR doesn't need to know 'this is Pro', just what's
 * allowed"), so we infer a coarse label for display only. Gating decisions
 * should always read `modules` / `raw.caps`, never this string.
 */
const inferPlan = (raw: MyEntitlements | null): PlanKey => {
  if (!raw) return 'free';
  if (raw.isFreeTier) return 'free';
  if (raw.caps.scribeMinutes === 'unlimited') return 'enterprise';
  if (raw.seatsPurchased > 1) return 'clinic';
  return 'pro';
};

const inferMode = (raw: MyEntitlements | null): TenantMode => {
  if (!raw) return 'solo';
  // Reflect actual headcount, not just purchased seats. A Free tenant
  // with 2 doctors but seatsPurchased=1 (Free's hard ceiling) is genuinely
  // a Practice — calling them "Solo" misrepresents the team.
  const headcount = Math.max(
    raw.activeMembers ?? 0,
    raw.seatsPurchased ?? 0,
    1,
  );
  if (headcount >= 5) return 'clinic';
  if (headcount > 1) return 'practice';
  return 'solo';
};

/**
 * Reads the active tenant snapshot from `/auth/me/entitlements` (cached
 * per tenant for 60 s) and ANDs it with the clinic's module settings.
 * Backend booleans override the free-tier defaults; a clinic switching
 * Appointments off then hides it even though the tier permits it — but
 * switching it on cannot buy it.
 */
export const useTenantEntitlements = (): TenantEntitlements => {
  const activeTenant = useAuthStore((s) => s.activeTenant);
  const isModuleEnabled = useFeatureToggleStore((s) => s.isModuleEnabled);

  const { data, isLoading } = useQuery({
    queryKey: ['me-entitlements', activeTenant?.id ?? 'no-tenant'],
    queryFn: () => entitlementsApi.me(),
    enabled: Boolean(activeTenant?.id),
    staleTime: 60_000,
    // Don't retry hard on 404 — that just means the user has no active
    // tenant context (platform staff, mid-onboarding). Fall back to
    // free-tier defaults silently.
    retry: (count, err) => {
      const status =
        (err as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === 401) return false;
      return count < 2;
    },
  });

  return useMemo<TenantEntitlements>(() => {
    const raw = data ?? null;

    // Free-tier defaults, then the backend entitlement, then AND in the
    // clinic's own choice (which can only subtract).
    const modules: Record<ModuleKey, boolean> = { ...FREE_DEFAULT };
    if (raw) {
      modules.whatsappBot = raw.whatsappBot;
      modules.telegramBot = raw.telegramBot;
      // Reports + resourceScheduling + multiSpecialty + customRoles +
      // visitingDoctors aren't gated by the entitlement booleans yet —
      // they'll get explicit flags when we add them. For now they stay
      // at the free-tier default.
    }
    // The clinic's own module choice can only ever turn something OFF.
    // It used to be assigned outright, which let a localStorage toggle
    // switch ON a module the subscription doesn't include — the store is
    // now server-backed and clinic-wide, which makes that inversion a
    // real entitlement bypass rather than a per-browser quirk.
    (Object.keys(LEGACY_MAP) as ModuleKey[]).forEach((k) => {
      const legacy = LEGACY_MAP[k];
      if (legacy) modules[k] = modules[k] && isModuleEnabled(legacy);
    });

    return {
      modules,
      plan: inferPlan(raw),
      mode: inferMode(raw),
      isLoading,
      raw,
    };
  }, [data, isLoading, isModuleEnabled]);
};

export const useTenantMode = (): TenantMode =>
  useTenantEntitlements().mode;

export const useHasModule = (key: ModuleKey): boolean =>
  useTenantEntitlements().modules[key];
